import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { EventListEntry, EventListResponse } from '@ni/shared';
import { useOrg } from '../api/app-context';
import { useEvents } from '../api/event-hooks';
import { RecordTable } from '../components/record/record-table';
import { LinkRow } from '../components/ui/row-link';
import { ErrorLine } from '../components/ui/error-line';
import { WorkingButton } from '../components/ui/working-button';
import { IfWrites } from '../components/write-access';
import { SEARCH_DEBOUNCE_MS, useSettled } from '../lib/debounce';
import { orgHasProtectionAccounts } from '../lib/vertical';
import {
  EVENTS_COLUMNS,
  EVENTS_EMPTY,
  EVENTS_NEW_ACTION,
  EVENTS_NONE_MATCH,
  EVENTS_SEARCH_LABEL,
  EVENTS_TITLE,
  LOADING_MORE,
  LOAD_MORE,
  eventDates,
} from '../lib/copy';
import { AccountChoice } from '../components/accounts/account-choice';
import { Loading } from '../components/ui/loading';
import { EmptyLine } from '../components/ui/empty-line';
import './events.css';

/**
 * **Events** (docs/notes/2026-09-23-event-graph-design.md §3a): the tracked
 * events, newest-touched first, name-searchable, with the account chip in a
 * security org. Every vertical has this page.
 */
export function Events() {
  const org = useOrg();
  const [typed, setTyped] = useState('');
  const [q, search] = useSettled('', SEARCH_DEBOUNCE_MS);
  const [params, setParams] = useSearchParams();
  const security = orgHasProtectionAccounts(org);
  const accountId = security ? params.get('account_id') : null;

  const asked = q.trim();
  const events = useEvents({
    ...(asked.length > 0 ? { q: asked } : {}),
    ...(accountId ? { account_id: accountId } : {}),
  });
  const entries = flattenEvents(events.data?.pages);

  return (
    <div className="column">
      <div className="head">
        <div className="titles">
          <div className="title">{EVENTS_TITLE}</div>
        </div>
        <div className="actions-right">
          <IfWrites>
            <Link
              className="btn hero"
              to={accountId ? `/events/new?account_id=${accountId}` : '/events/new'}
            >
              {EVENTS_NEW_ACTION}
            </Link>
          </IfWrites>
        </div>
      </div>

      {security ? (
        <AccountChoice
          value={accountId ?? ''}
          empty="All accounts"
          onChange={(id) => {
            const next = new URLSearchParams(params);
            if (id) next.set('account_id', id);
            else next.delete('account_id');
            setParams(next);
          }}
        />
      ) : null}

      <div className="events-tools">
        <input
          className="events-search"
          type="search"
          autoComplete="off"
          placeholder={EVENTS_SEARCH_LABEL}
          value={typed}
          onChange={(event) => {
            setTyped(event.target.value);
            search(event.target.value);
          }}
        />
      </div>

      <div className="section events-list">
        {events.isPending ? (
          <EmptyLine>
            <Loading />
          </EmptyLine>
        ) : entries.length === 0 ? (
          events.isError ? (
            <ErrorLine error={events.error} load />
          ) : (
            <EmptyLine>{asked ? EVENTS_NONE_MATCH : EVENTS_EMPTY}</EmptyLine>
          )
        ) : (
          <>
            <EventsTable entries={entries} account={security} />
            {events.hasNextPage || events.isError ? (
              <div className="list-foot">
                {events.hasNextPage ? (
                  <WorkingButton
                    label={LOAD_MORE}
                    workingLabel={LOADING_MORE}
                    working={events.isFetchingNextPage}
                    variant="act"
                    tiny
                    onClick={() => void events.fetchNextPage()}
                  />
                ) : null}
                {events.isError ? <ErrorLine error={events.error} load /> : null}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export function flattenEvents(pages: readonly EventListResponse[] | undefined): EventListEntry[] {
  return (pages ?? []).flatMap((page) => page.entries);
}

export function EventsTable({
  entries,
  account,
}: {
  entries: readonly EventListEntry[];
  account: boolean;
}) {
  const headings = account
    ? [EVENTS_COLUMNS.name, EVENTS_COLUMNS.account, EVENTS_COLUMNS.dates, EVENTS_COLUMNS.place]
    : [EVENTS_COLUMNS.name, EVENTS_COLUMNS.dates, EVENTS_COLUMNS.place];
  return (
    <RecordTable headings={headings} className="events-table">
      {entries.map((entry) => (
        <LinkRow key={entry.id} to={`/events/${entry.id}`}>
          <td className="event-name">
            <Link className="link" to={`/events/${entry.id}`}>
              {entry.display_name}
            </Link>
          </td>
          {account ? (
            <td className="quiet-cell">
              {entry.account ? (
                <Link className="link" to={`/accounts/${entry.account.id}`}>
                  {entry.account.label}
                </Link>
              ) : null}
            </td>
          ) : null}
          <td className="quiet-cell event-dates">
            {eventDates(entry.starts_on, entry.ends_on) ?? ''}
          </td>
          <td className="quiet-cell">{entry.place ?? ''}</td>
        </LinkRow>
      ))}
    </RecordTable>
  );
}
